import axios, { InternalAxiosRequestConfig } from 'axios';
import { tokenStore } from '../auth/tokenStore';

const API_AUTH =
  process.env.REACT_APP_ENV === 'production'
    ? 'https://authapi.vlab.live'
    : 'https://stg-authapi.vlab.live';

export { API_AUTH };

type FailureHandler = () => void;
let onAuthFailure: FailureHandler = () => {};
export const setOnAuthFailure = (fn: FailureHandler) => {
  onAuthFailure = fn;
};

export const authClient = axios.create({ baseURL: API_AUTH, withCredentials: true });
export const apiClient = axios.create({ withCredentials: true });

let refreshPromise: Promise<string | null> | null = null;

type QueueItem = {
  retry: () => void;
  reject: (e: unknown) => void;
};
const queue: QueueItem[] = [];

function flushQueue(token: string | null) {
  const items = queue.splice(0);
  if (!token) {
    items.forEach(({ reject }) => reject(new Error('Unauthorized')));
  } else {
    items.forEach(({ retry }) => retry());
  }
}

async function performRefresh(): Promise<string | null> {
  try {
    const resp = await authClient.post('/v1/refresh-token', {}, {
      _skipAuth: true,
    } as any);
    const token: string | undefined = resp?.data?.access_token;
    if (!token) return null;
    tokenStore.set(token);
    return token;
  } catch {
    return null;
  }
}

export async function refreshAccessToken(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = performRefresh().finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

function attachToken(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  if ((config as any)._skipAuth) return config;
  const token = tokenStore.get();
  if (token) {
    (config.headers as any).Authorization = `Bearer ${token}`;
  }
  return config;
}

function enforceJsonContentType(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  const method = config.method?.toLowerCase();
  if (method === 'post' || method === 'put') {
    if (!(config.headers as any)['Content-Type']) {
      (config.headers as any)['Content-Type'] = 'application/json';
    }
  }
  return config;
}

function setup(client: typeof apiClient) {
  client.interceptors.request.use(attachToken);
  client.interceptors.request.use(enforceJsonContentType);

  client.interceptors.response.use(
    (res) => res,
    async (error) => {
      const original = error.config;

      if (!error.response || error.response.status !== 401) throw error;
      if (original._retry) throw error;

      const url: string = original.url ?? '';
      if (url.includes('/v1/refresh-token')) {
        // refresh itself failed — clear token; outer interceptor will call onAuthFailure
        tokenStore.clear();
        throw error;
      }

      if (refreshPromise) {
        original._retry = true;
        return new Promise((resolve, reject) => {
          queue.push({
            retry: () => {
              original.headers.Authorization = `Bearer ${tokenStore.get()}`;
              resolve(client(original));
            },
            reject,
          });
        });
      }

      original._retry = true;
      const token = await refreshAccessToken();

      if (!token) {
        tokenStore.clear();
        flushQueue(null);
        onAuthFailure();
        throw error;
      }

      flushQueue(token);
      original.headers.Authorization = `Bearer ${token}`;
      return client(original);
    }
  );
}

setup(apiClient);
setup(authClient);
